import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import {
  updateAdherent,
  deleteAdherent,
} from "./../../../services/adherentServices";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Button from "../../../components/Button/Button";
import { FaPrint, FaFileExport } from "react-icons/fa";
import ButtonPrint from "../../../components/Button/ButtonPrint";
import generateAdherentPdf from "./AdherentPdfGenerator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import AdherentsCharts from "./AdherentsCharts";
import "react-toastify/dist/ReactToastify.css";

// Modales existantes
import EditAdherentAdminModal from "./EditAdherentAdminModal";
import CreateAdherentAdminModal from "./CreateAdherentAdminModal";
import ManageGroupModal from "../../PagesEnseignants/ListingAdherents/ManageGroupModal";

// Nouveaux modules
import useAdherentsAdmin from "./useAdherentsAdmin";
import FiltersBar from "./FiltersBar";
import TableCompact from "./TableCompact";
import VisualCards from "./VisualCards";

// Choix colonnes dans TableCompact
import ColumnPicker from "./ColumnPicker";
import {
  ALL_COLUMNS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  STORAGE_KEY,
} from "./columns.config";

// Helpers centralisés
import { getStatusColor } from "./helpers";

export default function ListingAdherentsAdmin() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Hook (data + filtres + liste filtrée)
  const { state, setters, handlers } = useAdherentsAdmin();
  const {
    adherents,
    loading,
    seasons,
    groups,
    groupsAll,
    dojos,
    selectedDojo,
    showGroupFilter,
    duplicateWarning,
    sortOrder,
    editingStatusId,
    visibleCount,
    scrollRef,
    searchTerm,
    seasonFilter,
    groupFilter,
    statusFilter,
    communeFilter,
    communes,
    filtered,
    activeSeason, // <-- ici
  } = state;
  const {
    setSearchTerm,
    setSeasonFilter,
    setGroupFilter,
    setStatusFilter,
    setCommuneFilter,
    setEditingStatusId,
  } = setters;
  const {
    handleDojoChange,
    toggleSortOrder,
    onScroll,
    refreshAll,
    setVisibleCount,
  } = handlers;

  // UI — vues & modales
  const [view, setView] = useState("compact"); // "compact" | "visual"
  const [showCharts, setShowCharts] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentAdherent, setCurrentAdherent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isChangeGroupModalOpen, setIsChangeGroupModalOpen] = useState(false);
  const [currentAdherentId, setCurrentAdherentId] = useState(null);

  // Choix des colonnes visibles
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch (e) {}
    return DEFAULT_VISIBLE_COLUMN_IDS;
  });


  // persistance
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumnIds));
    } catch (e) {}
  }, [visibleColumnIds]);

  const selectAll = () => setVisibleColumnIds(ALL_COLUMNS.map((c) => c.id));
  const selectNone = () => setVisibleColumnIds([]);
  const resetDefault = () => setVisibleColumnIds(DEFAULT_VISIBLE_COLUMN_IDS);

  // Actions
  const handleEditClick = (adherent) => {
    setCurrentAdherent(adherent);
    setShowEditModal(true);
  };
  const handleCloseModal = () => setShowEditModal(false);

  const handleEditSave = async (id, formData) => {
    try {
      await updateAdherent(id, formData);
      toast.success("Adhérent mis à jour avec succès");
      setShowEditModal(false);
      await refreshAll();
    } catch (e) {
      console.error("Erreur MAJ adhérent :", e);
      toast.error("Erreur lors de la mise à jour de l'adhérent");
    }
  };

  const handleDeleteClick = async (id) => {
    const a = adherents.find((x) => x.id === id);
    if (
      !window.confirm(
        `Supprimer l'adhérent ${a?.nom || ""} ${a?.prenom || ""} ?`
      )
    )
      return;
    try {
      await deleteAdherent(id);
      toast.success("Adhérent supprimé avec succès");
      await refreshAll();
    } catch (e) {
      console.error("Erreur suppression adhérent :", e);
      toast.error("Erreur lors de la suppression de l'adhérent");
    }
  };

  const openChangeGroupModal = (adherentId) => {
    setCurrentAdherentId(adherentId);
    setIsChangeGroupModalOpen(true);
  };
  const closeChangeGroupModal = async (refresh = false) => {
    setIsChangeGroupModalOpen(false);
    setCurrentAdherentId(null);
    if (refresh) await refreshAll();
  };

  const handleStatusChange = async (adherentId, newStatus) => {
    try {
      await updateAdherent(adherentId, { statut_inscription: newStatus });
      setEditingStatusId(null);
      toast.success("Statut mis à jour avec succès");
      await refreshAll();
    } catch (e) {
      console.error("Erreur MAJ statut :", e);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  // Exports
  const handleExportExcel = () => {
    const formatted = filtered.map((a) => {
      const row = { ...a };
      if (row.date_naissance) {
        row.date_naissance = new Date(row.date_naissance).toLocaleDateString(
          "fr-FR"
        );
      }
      row.Groups = (a.Groups || []).map((g) => g.nom).join(", ");
      row.Users = (a.Users || []).map((u) => `${u.nom} ${u.prenom}`).join(", ");
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Adherents");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "adherents_filtered.xlsx"
    );
  };

  const handlePrintPDF = () => {
    const doc = new jsPDF();
    const head = [["Nom", "Prénom", "Statut", "Email", "Naissance", "Groupes"]];
    const body = filtered.map((a) => [
      a.nom || "",
      a.prenom || "",
      a.statut_inscription || "",
      a.email || "",
      a.date_naissance
        ? new Date(a.date_naissance).toLocaleDateString("fr-FR")
        : "",
      (a.Groups || []).map((g) => g.nom).join(", "),
    ]);
    doc.text("Liste des adhérents (filtres appliqués)", 14, 15);
    doc.autoTable({ startY: 20, head, body });
    doc.save("adherents_filtered.pdf");
  };

  // Copier emails filtrés
  const copyFilteredEmails = useCallback(async () => {
    const emails = Array.from(
      new Set(
        filtered
          .map((a) => (a.email || "").trim())
          .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
      )
    );
    if (!emails.length) {
      toast.info("Aucun email à copier pour la sélection");
      return;
    }
    const text = emails.join(";");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Emails copiés (${emails.length})`);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(`Emails copiés (${emails.length})`);
      } catch (e) {
        console.error("Clipboard fallback failed:", e);
        toast.error("Impossible de copier dans le presse-papiers");
      }
    }
  }, [filtered]);

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Administrateur"
          title="Liste des Adhérents"
          align="center"
          className="mb-1"
        />

        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau + actions */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Gestion des adhérents (Création, Edition, Recherche, Filtres,
                Export)
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Sur cette page vous pouvez également changer le statut
                d'inscription, changer les groupes, ...
              </p>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                <Button
                  text="+ Créer un adhérent"
                  onClick={() => setShowCreateModal(true)}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
                <Button
                  text={
                    <>
                      <FaPrint className="mr-2" />
                      Imprimer PDF
                    </>
                  }
                  onClick={handlePrintPDF}
                  className="!bg-white !text-slate-900 !hover:bg-white/90 !rounded-xl !shadow-md"
                />
                <Button
                  text={
                    <>
                      <FaFileExport className="mr-2" />
                      Exporter Excel
                    </>
                  }
                  onClick={handleExportExcel}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
                <Button
                  text={
                    showCharts
                      ? "Masquer les graphiques"
                      : "Afficher les graphiques"
                  }
                  onClick={() => setShowCharts((v) => !v)}
                  className="!bg-white/90 !text-slate-900 !hover:bg-white !rounded-xl !shadow-md"
                />
                <ColumnPicker
                  className="!ml-2"
                  visibleIds={visibleColumnIds}
                  setVisibleIds={setVisibleColumnIds}
                  onReset={resetDefault}
                  onSelectAll={selectAll}
                  onSelectNone={selectNone}
                />
              </div>

              {duplicateWarning && (
                <div className="mt-4 max-w-4xl mx-auto rounded-xl bg-rose-600/90 px-4 py-2 text-sm ring-1 ring-white/40">
                  {duplicateWarning}
                </div>
              )}
            </div>
          </div>

          {/* Graphiques */}
          {showCharts && (
            <div className="px-5 pt-5 sm:px-8">
              <AdherentsCharts rows={filtered} groups={groupsAll} />
            </div>
          )}

          {/* Filtres */}
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            seasonFilter={seasonFilter}
            setSeasonFilter={setSeasonFilter}
            seasons={seasons}
            dojos={dojos}
            selectedDojo={selectedDojo}
            handleDojoChange={handleDojoChange}
            showGroupFilter={showGroupFilter}
            groups={groups}
            groupFilter={groupFilter}
            setGroupFilter={setGroupFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            communeFilter={communeFilter}
            setCommuneFilter={setCommuneFilter}
            communes={communes}
            filteredCount={filtered.length}
          />

          {/* Switch vues */}
          <div className="px-5 pb-3 sm:px-8 flex items-center justify-end gap-2">
            <button
              onClick={() => setView("compact")}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 ${
                view === "compact"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300"
              }`}
            >
              Affichage compact
            </button>
            <button
              onClick={() => setView("visual")}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 ${
                view === "visual"
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300"
              }`}
            >
              Affichage visuel
            </button>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun adhérent
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajustez vos filtres ou ajoutez des adhérents.
                </p>
              </div>
            )}

            {!loading &&
              filtered.length > 0 &&
              (view === "compact" ? (
                <TableCompact
                  rows={filtered.slice(0, visibleCount)}
                  totalCount={filtered.length}
                  visibleCount={visibleCount}
                  onScroll={onScroll}
                  scrollRef={scrollRef}
                  sortOrder={sortOrder}
                  toggleSortOrder={toggleSortOrder}
                  editingStatusId={editingStatusId}
                  setEditingStatusId={setEditingStatusId}
                  handleStatusChange={handleStatusChange}
                  openChangeGroupModal={openChangeGroupModal}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  copyFilteredEmails={copyFilteredEmails}
                  generateAdherentPdf={generateAdherentPdf}
                  getStatusColor={getStatusColor}
                  activeSeason={activeSeason}
                  visibleColumnIds={visibleColumnIds} // <-- NOUVEAU
                />
              ) : (
                <VisualCards
                  rows={filtered.slice(0, visibleCount)}
                  totalCount={filtered.length}
                  visibleCount={visibleCount}
                  onScroll={onScroll}
                  scrollRef={scrollRef}
                  editingStatusId={editingStatusId}
                  setEditingStatusId={setEditingStatusId}
                  handleStatusChange={handleStatusChange}
                  openChangeGroupModal={openChangeGroupModal}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  generateAdherentPdf={generateAdherentPdf}
                  getStatusColor={getStatusColor}
                  activeSeason={activeSeason}
                />
              ))}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modales */}
      {showEditModal && (
        <EditAdherentAdminModal
          isOpen={showEditModal}
          onClose={handleCloseModal}
          adherent={currentAdherent}
          refreshList={refreshAll}
          onSave={handleEditSave}
        />
      )}

      {showCreateModal && (
        <CreateAdherentAdminModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          refreshList={refreshAll}
          currentUser={user}
        />
      )}

      {isChangeGroupModalOpen && currentAdherentId && (
        <ManageGroupModal
          isOpen={isChangeGroupModalOpen}
          onClose={closeChangeGroupModal}
          adherentId={currentAdherentId}
          groups={groups}
          adherentGroups={
            adherents.find((x) => x.id === currentAdherentId)?.Groups || []
          }
          refreshData={refreshAll}
        />
      )}
    </section>
  );
}
